import React, { useRef, useState } from 'react';
import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  ButtonBase,
  IconButton,
  Popover,
  SvgIcon,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core';
import { LogOut as LogOutIcon } from 'react-feather';
import useAuth from 'src/hooks/useAuth';
import { getRoles } from 'src/contexts/Auth0Context';
import type { Theme } from 'src/theme';
import { normalizeRole } from '../NavBar/helpers';
import Settings from './Settings';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    padding: theme.spacing(1.5, 1),
    borderTop: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary
  },
  profile: {
    flex: '1 1 auto',
    minWidth: 0,
    justifyContent: 'flex-start',
    padding: theme.spacing(0.5, 1),
    borderRadius: 6,
    color: theme.palette.text.primary,
    '&:hover': { backgroundColor: theme.palette.action.hover },
    '&:focus-visible': { backgroundColor: theme.palette.action.focus }
  },
  avatar: {
    height: 32,
    width: 32,
    marginRight: theme.spacing(1),
    fontSize: 13,
    fontWeight: 600
  },
  popover: {
    width: 260,
    padding: theme.spacing(2),
    borderRadius: 12
  },
  label: {
    marginTop: theme.spacing(1.5),
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary
  },
  value: {
    wordBreak: 'break-all'
  }
}));

const ROLE_LABELS = { admin: 'Admin', manager: 'Manager', cms_user: 'User' };

// Auth0 fills `name` with the email for password accounts; show a readable
// name instead ("ashutosh.adhao@x.com" -> "Ashutosh Adhao").
export const displayName = (name = '', email = ''): string => {
  if (name && !name.includes('@')) return name;
  return (name || email)
    .split('@')[0]
    .split(/[._-]+/)
    .filter(Boolean)
    .map(part => part[0].toUpperCase() + part.slice(1))
    .join(' ');
};

// Sidebar footer: who is signed in, plus Settings and Logout.
const Account: FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const ref = useRef<any>(null);
  const { user, logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState<boolean>(false);

  const name = displayName(user.name, user.email);
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();
  const role = ROLE_LABELS[normalizeRole(getRoles())];

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      history.push('/');
    } catch (err) {
      console.error(err);
      enqueueSnackbar('Unable to logout', {
        variant: 'error'
      });
    }
  };

  return (
    <div className={classes.root}>
      <Tooltip title={t('Profile')}>
        <ButtonBase className={classes.profile} onClick={() => setOpen(true)} ref={ref}>
          <Avatar alt={name} className={classes.avatar} src={user.avatar}>
            {initials}
          </Avatar>
          <Typography variant="body2" noWrap>
            {name}
          </Typography>
        </ButtonBase>
      </Tooltip>
      <Popover
        // Opens upward: the profile sits in the sidebar footer.
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        classes={{ paper: classes.popover }}
        anchorEl={ref.current}
        onClose={() => setOpen(false)}
        open={isOpen}
      >
        <Typography variant="h5" component="h2" color="textPrimary">
          {t('Profile')}
        </Typography>
        {[
          { label: t('Name'), value: name },
          { label: t('Email'), value: user.email },
          { label: t('Role'), value: t(role) }
        ].map(({ label, value }) => (
          <React.Fragment key={label}>
            <div className={classes.label}>{label}</div>
            <Typography className={classes.value} variant="body2" color="textPrimary">
              {value}
            </Typography>
          </React.Fragment>
        ))}
      </Popover>
      <Settings />
      <Tooltip title={t('Logout')}>
        <IconButton color="inherit" onClick={handleLogout} aria-label={t('Logout')}>
          <SvgIcon fontSize="small">
            <LogOutIcon />
          </SvgIcon>
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default Account;
